import React, { useEffect, useState, useCallback } from 'react';
import {
  DollarSign, Plus, Trash2, Calculator, TrendingUp, TrendingDown,
  Building2, Receipt, Percent, RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';

const fmtUSD = (n) => `$${(n || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const fmtUSD2 = (n) => `$${(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// =====================================================================
// Financials Panel for a property — operating expenses, mortgage calc,
// and net cash flow toggleable Monthly / Annual.
// =====================================================================
export default function FinancialsPanel({ api, propertyId }) {
  const [config, setConfig] = useState(null);
  const [cashflow, setCashflow] = useState(null);
  const [view, setView] = useState('monthly'); // 'monthly' | 'annual'
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    if (!propertyId) return;
    try {
      const [f, c] = await Promise.all([
        api.get(`/properties/${propertyId}/financials`),
        api.get(`/properties/${propertyId}/cashflow`),
      ]);
      setConfig(f.data);
      setCashflow(c.data);
      setDirty(false);
    } catch (e) {
      // soft
    }
  }, [api, propertyId]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!config) return;
    setSaving(true);
    try {
      await api.put(`/properties/${propertyId}/financials`, {
        opex: config.opex,
        mortgage: config.mortgage,
        vacancy_rate: config.vacancy_rate,
        hoa_per_unit_monthly: config.hoa_per_unit_monthly || 0,
      });
      const c = await api.get(`/properties/${propertyId}/cashflow`);
      setCashflow(c.data);
      setDirty(false);
      toast.success('Financials saved');
    } catch (e) {
      toast.error('Save failed');
    }
    setSaving(false);
  };

  const updateOpex = (i, field, val) => {
    const next = [...(config.opex || [])];
    next[i] = { ...next[i], [field]: field === 'monthly' ? (parseFloat(val) || 0) : val };
    setConfig({ ...config, opex: next });
    setDirty(true);
  };
  const addOpex = (category = '') => {
    setConfig({ ...config, opex: [...(config.opex || []), { category, monthly: 0, notes: '' }] });
    setDirty(true);
  };
  const removeOpex = (i) => {
    const next = [...(config.opex || [])];
    next.splice(i, 1);
    setConfig({ ...config, opex: next });
    setDirty(true);
  };
  const updateMortgage = (field, val) => {
    setConfig({
      ...config,
      mortgage: {
        ...config.mortgage,
        [field]: field === 'term_years' ? (parseInt(val) || 0) : (parseFloat(val) || 0),
      },
    });
    setDirty(true);
  };

  const updateHoa = (val) => {
    setConfig({ ...config, hoa_per_unit_monthly: parseFloat(val) || 0 });
    setDirty(true);
  };

  if (!config || !cashflow) {
    return <div className="ceo-card-elevated p-5 text-sm" style={{ color: 'var(--muted)' }}>Loading financials…</div>;
  }

  const mult = view === 'annual' ? 12 : 1;
  const suffix = view === 'annual' ? '/yr' : '/mo';
  const net = view === 'annual' ? cashflow.net.annual : cashflow.net.monthly;
  const netPositive = net >= 0;
  const usedCats = new Set((config.opex || []).map(o => o.category));
  const unusedCats = (config.default_categories || []).filter(c => !usedCats.has(c));

  return (
    <div className="ceo-card-elevated p-5 space-y-5" data-testid="financials-panel">
      {/* Header with toggle */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <DollarSign className="h-5 w-5" style={{ color: 'var(--accent)' }} />
          <h3 className="text-base font-bold" style={{ color: 'var(--text)' }}>Financials & Cash Flow</h3>
          {dirty && <span className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: 'var(--warning, #f59e0b)' }}>· Unsaved</span>}
        </div>
        <div className="flex items-center gap-2">
          <div className="ceo-segment" data-testid="cashflow-view-toggle">
            <button onClick={() => setView('monthly')} className={`ceo-segment-btn ${view === 'monthly' ? 'active' : ''}`} data-testid="view-monthly">Monthly</button>
            <button onClick={() => setView('annual')} className={`ceo-segment-btn ${view === 'annual' ? 'active' : ''}`} data-testid="view-annual">Annual</button>
          </div>
          <button onClick={save} disabled={!dirty || saving} className="ceo-btn-primary disabled:opacity-50 px-4" data-testid="save-financials">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      {/* === Big headline cards === */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard
          label="Effective Revenue"
          value={fmtUSD(cashflow.revenue.effective_rent_monthly * mult)}
          sub={cashflow.revenue.hoa_income_monthly > 0
            ? `${cashflow.revenue.rental_units} rentals · +${fmtUSD(cashflow.revenue.hoa_income_monthly * mult)} HOA`
            : `${cashflow.revenue.rental_units} rental units · ${cashflow.revenue.vacancy_rate_pct}% vacancy`}
          icon={<Building2 className="h-4 w-4" />}
          color="#22c55e"
          testid="kpi-revenue"
        />
        <KpiCard
          label="Operating Expenses"
          value={fmtUSD(cashflow.opex.monthly * mult)}
          sub={`${(config.opex || []).length} categories`}
          icon={<Receipt className="h-4 w-4" />}
          color="#f59e0b"
          testid="kpi-opex"
        />
        <KpiCard
          label="Debt Service (P&I)"
          value={fmtUSD(cashflow.mortgage.total_monthly * mult)}
          sub={cashflow.mortgage.principal > 0
            ? `${cashflow.mortgage.interest_rate}% · ${cashflow.mortgage.term_years}yr`
            : 'No mortgage set'}
          icon={<Calculator className="h-4 w-4" />}
          color="#3b82f6"
          testid="kpi-mortgage"
        />
        <KpiCard
          label={`Net Cash Flow ${suffix}`}
          value={fmtUSD(net)}
          sub={netPositive ? 'Positive' : 'Negative · review opex'}
          icon={netPositive ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
          color={netPositive ? '#16a34a' : '#dc2626'}
          big
          testid="kpi-net"
        />
      </div>

      {/* === Cash flow waterfall === */}
      <div className="rounded-md p-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
        <h4 className="text-xs uppercase tracking-widest font-semibold mb-3" style={{ color: 'var(--muted)' }}>Cash flow waterfall · {view}</h4>
        <div className="space-y-1.5 text-sm">
          <WaterfallRow label="Gross rental income" value={cashflow.revenue.gross_rent_monthly * mult} kind="add" />
          <WaterfallRow label={`Vacancy loss (${cashflow.revenue.vacancy_rate_pct}%)`} value={cashflow.revenue.vacancy_loss_monthly * mult} kind="sub" />
          {cashflow.revenue.hoa_income_monthly > 0 && (
            <WaterfallRow
              label={`HOA income ($${cashflow.revenue.hoa_per_unit_monthly}/unit × ${cashflow.revenue.hoa_contributing_units} condos)`}
              value={cashflow.revenue.hoa_income_monthly * mult}
              kind="add"
            />
          )}
          <WaterfallRow label="= Effective revenue" value={cashflow.revenue.effective_rent_monthly * mult} kind="subtotal" />
          <WaterfallRow label="Operating expenses" value={cashflow.opex.monthly * mult} kind="sub" />
          <WaterfallRow label="= Net Operating Income (NOI)" value={cashflow.noi.monthly * mult} kind="subtotal" />
          {cashflow.mortgage.total_monthly > 0 && (
            <WaterfallRow label="Debt service (mortgage P&I)" value={cashflow.mortgage.total_monthly * mult} kind="sub" />
          )}
          <WaterfallRow label="= Net Cash Flow" value={net} kind="total" />
        </div>
        {(cashflow.metrics.cap_rate_pct !== null || cashflow.metrics.dscr !== null) && (
          <div className="mt-4 pt-3 border-t flex flex-wrap items-center gap-4" style={{ borderColor: 'var(--border)' }}>
            {cashflow.metrics.cap_rate_pct !== null && (
              <Metric label="Cap rate" value={`${cashflow.metrics.cap_rate_pct}%`} hint="NOI ÷ purchase price · 8%+ is strong" />
            )}
            {cashflow.metrics.dscr !== null && (
              <Metric
                label="DSCR"
                value={cashflow.metrics.dscr.toFixed(2)}
                hint="Debt-service coverage · 1.25+ is bankable"
                accent={cashflow.metrics.dscr >= 1.25 ? '#16a34a' : cashflow.metrics.dscr >= 1.0 ? '#f59e0b' : '#dc2626'}
              />
            )}
          </div>
        )}
      </div>

      {/* === OpEx editor + Mortgage calc grid === */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Operating Expenses */}
        <div data-testid="opex-editor">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-bold flex items-center gap-1.5" style={{ color: 'var(--text)' }}>
              <Receipt className="h-4 w-4" /> Operating Expenses
              <span className="text-xs font-normal opacity-60">· {fmtUSD(cashflow.opex.monthly)}/mo</span>
            </h4>
            <div className="flex items-center gap-2 flex-wrap">
              <label className="text-xs flex items-center gap-1" style={{ color: 'var(--muted)' }}>
                Vacancy
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.5"
                  className="ceo-input !w-16 !py-1 !text-xs"
                  value={config.vacancy_rate || 0}
                  onChange={(e) => { setConfig({ ...config, vacancy_rate: parseFloat(e.target.value) || 0 }); setDirty(true); }}
                  data-testid="vacancy-rate"
                />
                <Percent className="h-3 w-3" />
              </label>
              <label className="text-xs flex items-center gap-1" style={{ color: 'var(--muted)' }} title={`Collected from ${cashflow.revenue.hoa_contributing_units} condo unit(s) (status: For Sale / Sold / Reserved / Rent or Buy)`}>
                HOA/unit
                <span className="text-[10px] opacity-50">$</span>
                <input
                  type="number"
                  min="0"
                  step="25"
                  className="ceo-input !w-20 !py-1 !text-xs"
                  value={config.hoa_per_unit_monthly || 0}
                  onChange={(e) => updateHoa(e.target.value)}
                  data-testid="hoa-per-unit"
                />
                <span className="text-[10px] opacity-50">/mo</span>
              </label>
            </div>
          </div>

          {cashflow.revenue.hoa_contributing_units > 0 && (config.hoa_per_unit_monthly || 0) > 0 && (
            <div className="text-[11px] mb-2 italic" style={{ color: 'var(--text-dim)' }}>
              ↳ Generates <strong style={{ color: '#22c55e' }}>{fmtUSD(cashflow.revenue.hoa_income_monthly)}/mo</strong> · <strong style={{ color: '#22c55e' }}>{fmtUSD(cashflow.revenue.hoa_income_monthly * 12)}/yr</strong> in HOA revenue from {cashflow.revenue.hoa_contributing_units} condo units.
            </div>
          )}

          <div className="space-y-1.5 max-h-[420px] overflow-y-auto pr-1">
            {(config.opex || []).map((line, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`opex-row-${i}`}>
                <input
                  type="text"
                  className="ceo-input flex-1 !py-1.5 !text-sm"
                  placeholder="Category"
                  value={line.category}
                  onChange={(e) => updateOpex(i, 'category', e.target.value)}
                />
                <div className="relative">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs opacity-60">$</span>
                  <input
                    type="number"
                    min="0"
                    step="50"
                    className="ceo-input !w-28 !py-1.5 !pl-5 !text-sm text-right"
                    placeholder="0"
                    value={line.monthly || ''}
                    onChange={(e) => updateOpex(i, 'monthly', e.target.value)}
                    data-testid={`opex-amount-${i}`}
                  />
                </div>
                <button onClick={() => removeOpex(i)} className="ceo-btn-icon" title="Remove">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
            {(config.opex || []).length === 0 && (
              <p className="text-xs italic py-3 text-center" style={{ color: 'var(--muted)' }}>No expenses yet. Add some below.</p>
            )}
          </div>

          <div className="mt-3 space-y-2">
            <button onClick={() => addOpex('')} className="ceo-btn-secondary text-xs" data-testid="add-custom-opex">
              <Plus className="h-3 w-3 inline mr-1" /> Add custom line
            </button>
            {unusedCats.length > 0 && (
              <div>
                <p className="text-[10px] uppercase tracking-widest mb-1.5" style={{ color: 'var(--muted)' }}>Quick-add:</p>
                <div className="flex flex-wrap gap-1">
                  {unusedCats.map(c => (
                    <button key={c} onClick={() => addOpex(c)} className="ceo-pill !text-[11px]" data-testid={`quickadd-${c}`}>
                      + {c}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Mortgage Calculator */}
        <div data-testid="mortgage-calc">
          <h4 className="text-sm font-bold mb-2 flex items-center gap-1.5" style={{ color: 'var(--text)' }}>
            <Calculator className="h-4 w-4" /> Mortgage Calculator
            {cashflow.mortgage.total_monthly > 0 && (
              <span className="text-xs font-normal opacity-60">· {fmtUSD2(cashflow.mortgage.monthly_pi)}/mo P&I</span>
            )}
          </h4>

          <div className="grid grid-cols-2 gap-3">
            <MortgageInput label="Purchase / Project cost" value={config.mortgage.principal} onChange={v => updateMortgage('principal', v)} prefix="$" testid="mort-principal" step={10000} />
            <MortgageInput label="Down payment" value={config.mortgage.down_payment} onChange={v => updateMortgage('down_payment', v)} prefix="$" testid="mort-down" step={1000} />
            <MortgageInput label="Interest rate" value={config.mortgage.interest_rate} onChange={v => updateMortgage('interest_rate', v)} suffix="%" testid="mort-rate" step={0.125} />
            <MortgageInput label="Term" value={config.mortgage.term_years} onChange={v => updateMortgage('term_years', v)} suffix="yr" testid="mort-term" step={1} integer />
            <MortgageInput label="Extra principal" value={config.mortgage.extra_monthly} onChange={v => updateMortgage('extra_monthly', v)} prefix="$" suffix="/mo" testid="mort-extra" step={50} />
          </div>

          <div className="mt-4 rounded-md p-3 space-y-1.5 text-sm" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
            <SummaryRow label="Loan amount" value={fmtUSD(cashflow.mortgage.loan_amount)} />
            <SummaryRow label="Monthly P&I" value={fmtUSD2(cashflow.mortgage.monthly_pi)} bold />
            {cashflow.mortgage.extra_monthly > 0 && (
              <SummaryRow label="+ Extra principal" value={fmtUSD2(cashflow.mortgage.extra_monthly)} />
            )}
            <SummaryRow label="Total annual debt service" value={fmtUSD(cashflow.mortgage.total_annual)} bold />
            {cashflow.mortgage.loan_amount > 0 && (
              <SummaryRow
                label="Lifetime interest"
                value={fmtUSD((cashflow.mortgage.monthly_pi * cashflow.mortgage.term_years * 12) - cashflow.mortgage.loan_amount)}
                hint="Total interest paid over loan term"
              />
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-end pt-1">
        <button onClick={load} className="ceo-btn-secondary text-xs flex items-center gap-1" data-testid="reload-cashflow">
          <RefreshCw className="h-3 w-3" /> Reload from server
        </button>
      </div>
    </div>
  );
}

// ---------- subcomponents ----------

function KpiCard({ label, value, sub, icon, color, big, testid }) {
  return (
    <div
      className={`rounded-md p-3 ${big ? 'md:col-span-1' : ''}`}
      style={{
        background: 'var(--surface)',
        border: `1px solid ${color}33`,
        boxShadow: big ? `0 0 0 1px ${color}22 inset` : 'none',
      }}
      data-testid={testid}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: 'var(--muted)' }}>{label}</span>
        <span style={{ color }}>{icon}</span>
      </div>
      <div className={`font-bold ${big ? 'text-2xl' : 'text-xl'}`} style={{ color }}>{value}</div>
      {sub && <div className="text-[11px] mt-0.5 opacity-70" style={{ color: 'var(--text)' }}>{sub}</div>}
    </div>
  );
}

function WaterfallRow({ label, value, kind }) {
  const isAdd = kind === 'add';
  const isSub = kind === 'sub';
  const isSubtotal = kind === 'subtotal';
  const isTotal = kind === 'total';
  const sign = isSub ? '−' : isAdd ? '+' : '';
  const display = isSub ? `(${fmtUSD(Math.abs(value))})` : fmtUSD(Math.abs(value));
  const color = isTotal
    ? (value >= 0 ? '#16a34a' : '#dc2626')
    : isSub
      ? '#dc2626'
      : isSubtotal
        ? 'var(--accent)'
        : 'var(--text)';
  return (
    <div className={`flex items-center justify-between ${isTotal ? 'pt-2 border-t mt-1' : ''} ${isSubtotal ? 'font-semibold' : ''} ${isTotal ? 'font-bold text-base' : ''}`}
      style={{ borderColor: 'var(--border)' }}>
      <span style={{ color: isTotal || isSubtotal ? color : 'var(--text)' }}>{label}</span>
      <span style={{ color }} className={isTotal ? 'tabular-nums' : 'tabular-nums opacity-90'}>
        {sign && !isSub && <span className="opacity-60 mr-0.5">{sign}</span>}
        {display}
      </span>
    </div>
  );
}

function Metric({ label, value, hint, accent }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: 'var(--muted)' }}>{label}</div>
      <div className="text-lg font-bold tabular-nums" style={{ color: accent || 'var(--text)' }}>{value}</div>
      {hint && <div className="text-[10px] opacity-60 italic">{hint}</div>}
    </div>
  );
}

function MortgageInput({ label, value, onChange, prefix, suffix, step, integer, testid }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-widest font-semibold mb-1" style={{ color: 'var(--muted)' }}>{label}</label>
      <div className="relative">
        {prefix && <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs opacity-60">{prefix}</span>}
        <input
          type="number"
          min="0"
          step={step}
          value={value || ''}
          onChange={e => onChange(integer ? parseInt(e.target.value) || 0 : parseFloat(e.target.value) || 0)}
          className={`ceo-input w-full !py-1.5 ${prefix ? '!pl-5' : ''} ${suffix ? '!pr-10' : ''}`}
          placeholder="0"
          data-testid={testid}
        />
        {suffix && <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs opacity-60">{suffix}</span>}
      </div>
    </div>
  );
}

function SummaryRow({ label, value, bold, hint }) {
  return (
    <div className={`flex justify-between items-baseline ${bold ? 'font-semibold' : ''}`}>
      <span style={{ color: 'var(--muted)' }} className="text-xs">{label}{hint && <span className="opacity-60 italic ml-1">· {hint}</span>}</span>
      <span style={{ color: 'var(--text)' }} className="tabular-nums">{value}</span>
    </div>
  );
}
